/*
 * EngineStoreClient — the REAL data-layer client (Phase 0/1 swap-in).
 * ============================================================================
 * Implements `StoreClient` by fetch()ing the engine/store BRIDGE described in
 * `standalone/BRIDGE-CONTRACT.md` (base http://127.0.0.1:8787 by default).
 *
 * Honesty guarantees this client bakes in (never fake a value):
 *  - Every read GETs its endpoint and returns the bridge's `Stamped<T>` envelope
 *    verbatim (shape-validated defensively). Staleness/offline is the bridge's
 *    call — we surface it, we never upgrade it.
 *  - `POST /api/chat` 503 (engine overloaded) → a typed RETRYABLE error, never a
 *    fabricated reply.
 *  - `POST /api/action/decide` / `POST /api/update/ack` 501 (not wired) → a typed
 *    NOT-WIRED error the surface renders as "coming soon", never a fake success.
 *
 * Identity is carried per the contract as `?who=` AND `X-Skippy-Who`; the server
 * enforces the scope firewall. The client never lets the user pick `who` at the
 * surface — it is fixed at construction (default "nick").
 */

import type { StoreClient } from './StoreClient';
import type {
  ActionCard,
  Business,
  ChatMessage,
  Finances,
  Flag,
  Health,
  Identity,
  MacArm,
  School,
  Settings,
  ShoppingItem,
  Stamped,
  Today,
  TodoItem,
  UpdateCard,
  CalendarEvent,
} from './types';

/** Discriminates the failure modes a surface may want to render differently. */
export type BridgeErrorKind =
  | 'not-wired' // 501 — feature exists in the contract but has no live backend yet
  | 'overloaded' // 503 — engine transiently overloaded (Anthropic 529); retryable
  | 'http' // any other non-2xx
  | 'network' // fetch() threw (bridge down / CORS / DNS)
  | 'shape'; // 2xx but the body didn't match the expected envelope

/**
 * Typed, catchable error for every bridge failure. Surfaces can branch on
 * `.kind` (or the convenience booleans) to render the honest state:
 *   - `notWired`  → "not wired yet / coming soon" (disabled control)
 *   - `retryable` → "engine busy, try again" (retry affordance)
 */
export class BridgeError extends Error {
  readonly kind: BridgeErrorKind;
  readonly status?: number;
  readonly endpoint: string;

  constructor(kind: BridgeErrorKind, message: string, endpoint: string, status?: number) {
    super(message);
    this.name = 'BridgeError';
    this.kind = kind;
    this.endpoint = endpoint;
    this.status = status;
    // Restore prototype chain for reliable `instanceof` under transpilation.
    Object.setPrototypeOf(this, BridgeError.prototype);
  }

  /** True when the feature is defined but has no live backend yet (HTTP 501). */
  get notWired(): boolean {
    return this.kind === 'not-wired';
  }

  /** True when retrying the same call may succeed (HTTP 503 / engine overloaded). */
  get retryable(): boolean {
    return this.kind === 'overloaded';
  }
}

export class EngineStoreClient implements StoreClient {
  private readonly baseUrl: string;
  private readonly who: string;
  /** Local mirror of the chat log — an optimistic fallback if the history GET
   *  can't reach the bridge (so a dropped connection doesn't wipe the view). */
  private chatMirror: ChatMessage[] = [];

  constructor(baseUrl: string, who = 'nick') {
    // Normalize: strip a single trailing slash so `${base}/api/...` never doubles.
    this.baseUrl = baseUrl.replace(/\/+$/, '');
    this.who = who || 'nick';
  }

  // ---- internals ---------------------------------------------------------

  private url(path: string): string {
    const sep = path.includes('?') ? '&' : '?';
    return `${this.baseUrl}${path}${sep}who=${encodeURIComponent(this.who)}`;
  }

  private headers(extra?: Record<string, string>): Record<string, string> {
    return {
      'X-Skippy-Who': this.who,
      Accept: 'application/json',
      ...extra,
    };
  }

  /** Fetch + parse JSON, mapping transport/HTTP failures to typed BridgeErrors. */
  private async requestJson(
    path: string,
    init?: RequestInit,
  ): Promise<unknown> {
    let res: Response;
    try {
      res = await fetch(this.url(path), init);
    } catch (e) {
      throw new BridgeError(
        'network',
        `bridge unreachable at ${this.baseUrl}${path}: ${(e as Error)?.message ?? 'fetch failed'}`,
        path,
      );
    }

    if (!res.ok) {
      // Preserve the two contract-significant statuses as distinct kinds.
      if (res.status === 501) {
        throw new BridgeError('not-wired', `not wired yet: ${path}`, path, 501);
      }
      if (res.status === 503) {
        throw new BridgeError('overloaded', `engine overloaded: ${path}`, path, 503);
      }
      throw new BridgeError('http', `HTTP ${res.status} from ${path}`, path, res.status);
    }

    // 204 No Content (e.g. a successful ack) has no body to parse.
    if (res.status === 204) return null;
    const raw = await res.text();
    if (raw.trim() === '') return null;
    try {
      return JSON.parse(raw);
    } catch {
      throw new BridgeError('shape', `invalid JSON body from ${path}`, path, res.status);
    }
  }

  /** Validate the Stamped<T> envelope shape defensively, then hand it back. */
  private asStamped<T>(body: unknown, path: string): Stamped<T> {
    if (
      !body ||
      typeof body !== 'object' ||
      !('data' in body) ||
      !('generated_at' in body) ||
      !('source' in body) ||
      !('freshness' in body)
    ) {
      throw new BridgeError('shape', `response from ${path} is not a Stamped envelope`, path);
    }
    return body as Stamped<T>;
  }

  private async getStamped<T>(path: string): Promise<Stamped<T>> {
    const body = await this.requestJson(path);
    return this.asStamped<T>(body, path);
  }

  // ---- identity ----------------------------------------------------------

  async getIdentity(): Promise<Identity> {
    const body = await this.requestJson('/api/identity');
    if (
      !body ||
      typeof body !== 'object' ||
      !('id' in body) ||
      !('persona' in body) ||
      !('scopes' in body)
    ) {
      throw new BridgeError('shape', 'identity response missing id/persona/scopes', '/api/identity');
    }
    return body as Identity;
  }

  // ---- reads (all Stamped<T>) --------------------------------------------

  getToday(): Promise<Stamped<Today>> {
    return this.getStamped<Today>('/api/today');
  }

  getActions(): Promise<Stamped<ActionCard[]>> {
    return this.getStamped<ActionCard[]>('/api/actions');
  }

  getUpdates(): Promise<Stamped<UpdateCard[]>> {
    return this.getStamped<UpdateCard[]>('/api/updates');
  }

  getHealth(): Promise<Stamped<Health>> {
    return this.getStamped<Health>('/api/health');
  }

  getFinances(): Promise<Stamped<Finances>> {
    return this.getStamped<Finances>('/api/finances');
  }

  getBusiness(): Promise<Stamped<Business>> {
    return this.getStamped<Business>('/api/business');
  }

  getCalendar(): Promise<Stamped<CalendarEvent[]>> {
    return this.getStamped<CalendarEvent[]>('/api/calendar');
  }

  getTodos(): Promise<Stamped<TodoItem[]>> {
    return this.getStamped<TodoItem[]>('/api/todos');
  }

  getShopping(): Promise<Stamped<ShoppingItem[]>> {
    return this.getStamped<ShoppingItem[]>('/api/shopping');
  }

  getExtras(): Promise<Stamped<MacArm>> {
    return this.getStamped<MacArm>('/api/extras');
  }

  getSchool(): Promise<Stamped<School>> {
    return this.getStamped<School>('/api/school');
  }

  getSettings(): Promise<Stamped<Settings>> {
    return this.getStamped<Settings>('/api/settings');
  }

  getCritic(): Promise<Stamped<Flag[]>> {
    // The bridge returns an honest offline envelope (empty data) when the critic
    // hasn't run; we surface that as-is, never upgrading it to fresh.
    return this.getStamped<Flag[]>('/api/critic');
  }

  // ---- chat --------------------------------------------------------------

  async getChatHistory(): Promise<ChatMessage[]> {
    try {
      const body = await this.requestJson('/api/chat/history');
      if (!Array.isArray(body)) {
        throw new BridgeError('shape', 'chat history is not an array', '/api/chat/history');
      }
      const history = body as ChatMessage[];
      this.chatMirror = history;
      return history;
    } catch (e) {
      // Honest degrade: if the bridge is unreachable, hand back the local mirror
      // rather than throwing away a conversation the user can still see.
      if (e instanceof BridgeError && e.kind === 'network') {
        return this.chatMirror;
      }
      throw e;
    }
  }

  async sendChat(text: string, lane: 'DEEP' | 'FAST'): Promise<ChatMessage> {
    // Mirror the user turn optimistically before the round-trip.
    const userTurn: ChatMessage = { id: `u${Date.now()}`, role: 'user', text, lane };
    this.chatMirror = [...this.chatMirror, userTurn];

    const body = await this.requestJson('/api/chat', {
      method: 'POST',
      headers: this.headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ text, lane, who: this.who }),
    });

    if (
      !body ||
      typeof body !== 'object' ||
      !('id' in body) ||
      !('role' in body) ||
      !('text' in body)
    ) {
      throw new BridgeError('shape', 'chat reply missing id/role/text', '/api/chat');
    }
    const reply = body as ChatMessage;
    this.chatMirror = [...this.chatMirror, reply];
    return reply;
  }

  // ---- writes ------------------------------------------------------------

  async decideAction(
    id: string,
    decision: 'approve' | 'edit' | 'skip',
    editedText?: string,
  ): Promise<ActionCard> {
    const body = await this.requestJson('/api/action/decide', {
      method: 'POST',
      headers: this.headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ id, decision, editedText }),
    });
    if (!body || typeof body !== 'object' || !('id' in body)) {
      throw new BridgeError('shape', 'action decision reply is not an ActionCard', '/api/action/decide');
    }
    return body as ActionCard;
  }

  async ackUpdate(id: string): Promise<void> {
    // 204 = success (no body); 501 → BridgeError('not-wired') via requestJson's
    // status check, which runs before any body parse.
    await this.requestJson('/api/update/ack', {
      method: 'POST',
      headers: this.headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ id }),
    });
  }

  async registerPush(token: string, platform: string): Promise<void> {
    // POST the device token to the bridge (ala-state/push-tokens.json). Best-effort:
    // any bridge failure surfaces as a thrown BridgeError the caller swallows — a
    // failed push registration must never break the app. No response body is needed.
    await this.requestJson('/api/push/register', {
      method: 'POST',
      headers: this.headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ token, platform, who: this.who }),
    });
  }
}
